// THE single source of truth for the four resource icons: palette, labels, and
// the icon GEOMETRY, parameterised by radius. Pure — no React, no Pixi — so both
// the sidebar's SVG icons (resources.tsx) and the board's canvas tokens
// (game/PixiGame.ts) build from the SAME shapes. A resource therefore looks
// identical everywhere, at any size; change a shape here and both follow.
//
// All geometry is centred at the origin (0,0) and scales with the radius `r`.

export const RESOURCE_KINDS = ["minerals", "food", "vigor", "obsidian"] as const;
export type ResourceKind = (typeof RESOURCE_KINDS)[number];

export const RESOURCE_COLORS: Record<ResourceKind, string> = {
  minerals: "#3f7fd0",
  food: "#5aa84f",
  vigor: "#d8443f",
  obsidian: "#33333f",
};

export const RESOURCE_LABELS: Record<ResourceKind, string> = {
  minerals: "Mineral",
  food: "Food",
  vigor: "Vigor",
  obsidian: "Obsidian",
};

/** Public URL of the pre-rendered PNG icon for a resource, served from
 * `client/public/resources`. The permanent and one-time looks are separate art
 * files. Note the files use the singular "mineral". Used by both the React icons
 * (resources.tsx) and the board's canvas tokens (game/PixiGame.ts). */
export function resourceIconUrl(kind: string, oneTime = false): string {
  const file = kind === "minerals" ? "mineral" : kind;
  return `/resources/${file}_${oneTime ? "one-time" : "permanent"}.png`;
}

export type Point = [number, number];

/** Food renders as a disc; its radius is this fraction of the icon radius `r`. */
export const FOOD_RADIUS_FACTOR = 0.92;

/** Minerals — pointy-top hexagon gem, vertices at radius `r`. */
export function hexPoints(r: number): Point[] {
  const out: Point[] = [];
  for (let i = 0; i < 6; i++) {
    const a = (Math.PI / 180) * (60 * i - 90);
    out.push([r * Math.cos(a), r * Math.sin(a)]);
  }
  return out;
}

/** Obsidian — upright diamond (rhombus), taller than wide. */
export function diamondPoints(r: number): Point[] {
  return [
    [0, -r],
    [r * 0.72, 0],
    [0, r],
    [-r * 0.72, 0],
  ];
}

/** Sample a cubic Bézier into `n` points (excluding the start), appended to `out`. */
function sampleCubic(p0: Point, p1: Point, p2: Point, p3: Point, n: number, out: Point[]): void {
  for (let i = 1; i <= n; i++) {
    const t = i / n;
    const u = 1 - t;
    out.push([
      u * u * u * p0[0] + 3 * u * u * t * p1[0] + 3 * u * t * t * p2[0] + t * t * t * p3[0],
      u * u * u * p0[1] + 3 * u * u * t * p1[1] + 3 * u * t * t * p2[1] + t * t * t * p3[1],
    ]);
  }
}

/** Vigor — heart (point down) as a sampled closed polygon, so the SVG and the
 * canvas trace the identical outline. `samples` points per Bézier lobe. */
export function heartPoints(r: number, samples = 14): Point[] {
  const out: Point[] = [[0, r * 0.72]];
  sampleCubic([0, r * 0.72], [r * 1.12, -r * 0.08], [r * 0.56, -r * 0.98], [0, -r * 0.28], samples, out);
  sampleCubic([0, -r * 0.28], [-r * 0.56, -r * 0.98], [-r * 1.12, -r * 0.08], [0, r * 0.72], samples, out);
  return out;
}

/** The closed outline polygon for a resource icon at radius `r`, centred at the
 * origin — or `null` for `food` (and any unknown kind), which is a disc each
 * renderer draws natively at radius `r * FOOD_RADIUS_FACTOR`. */
export function resourceOutline(kind: string, r: number): Point[] | null {
  switch (kind) {
    case "minerals":
      return hexPoints(r);
    case "vigor":
      return heartPoints(r);
    case "obsidian":
      return diamondPoints(r);
    default:
      return null; // food + fallback → disc
  }
}
